import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { PermissionsService } from '../../../shared/services/permissions.service';
import { ScopeDTO } from '../../../shared/types/permission.types';
import { PermissionsTabsComponent } from '../permissions-tabs/permissions-tabs.component';

@Component({
  selector: 'app-scopes-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalComponent,
    ConfirmModalComponent,
    PermissionsTabsComponent,
    PageHeaderComponent,
    DataStateComponent,
  ],
  templateUrl: './scopes-list.component.html',
})
export class ScopesListComponent implements OnInit {
  private readonly permissionsService = inject(PermissionsService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly scopes = signal<ScopeDTO[]>([]);

  // create modal
  readonly createOpen = signal(false);
  readonly creating = signal(false);
  readonly createError = signal<string | null>(null);
  readonly createForm = this.fb.group({
    name: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_\-.:]+$/)]],
    displayName: [''],
    iconUri: [''],
  });

  // delete modal
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);
  readonly toDelete = signal<ScopeDTO | null>(null);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.permissionsService
      .listScopes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.scopes.set(res ?? []);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.scopes.set([]);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  // create
  openCreate(): void {
    this.createError.set(null);
    this.createForm.reset({ name: '', displayName: '', iconUri: '' });
    this.createOpen.set(true);
  }
  closeCreate(): void { if (this.creating()) return; this.createOpen.set(false); }
  submitCreate(): void {
    if (this.createForm.invalid || this.creating()) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.creating.set(true);
    this.createError.set(null);
    const raw = this.createForm.getRawValue();
    this.permissionsService
      .createScope({
        name: raw.name.trim(),
        displayName: raw.displayName?.trim() || undefined,
        iconUri: raw.iconUri?.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.creating.set(false);
          this.createOpen.set(false);
          this.load();
        },
        error: (err: unknown) => {
          this.creating.set(false);
          this.createError.set(
            toErrorMessage(err, 'La création a échoué.', {
              409: 'Un scope avec ce nom existe déjà.',
            }),
          );
        },
      });
  }

  // delete
  openDelete(scope: ScopeDTO): void {
    this.toDelete.set(scope);
    this.deleteError.set(null);
    this.deleteOpen.set(true);
  }
  closeDelete(): void { this.deleteOpen.set(false); }
  confirmDelete(): void {
    const scope = this.toDelete();
    if (!scope?.id) return;
    this.deleting.set(true);
    this.deleteError.set(null);
    this.permissionsService.deleteScope(scope.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        this.load();
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }

  showCreateError(name: keyof typeof this.createForm.controls): boolean {
    const c = this.createForm.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }
}
